/**
 * View model of a message analysis (platform API `GET …/analysis`, module message-analysis).
 * Declared here structurally so packages/ui does not depend on the API client; the api-client type
 * `MessageAnalysis` is assignable to it.
 */
import type { PolicyEvaluationEvidence } from '../connected/policy-evaluation';
import type { Status } from '../composites/states';

export type AnalysisSeverity = 'pass' | 'info' | 'warning' | 'fail';

export type FindingCode =
  | 'source.exported'
  | 'source.reconstructed'
  | 'source.headersOnly'
  | 'delivery.local'
  | 'delivery.noTls'
  | 'delivery.slow'
  | 'auth.notChecked'
  | 'auth.foreignResults'
  | 'auth.spfFail'
  | 'auth.dkimFail'
  | 'auth.dmarcPass'
  | 'auth.dmarcFail'
  | 'scanner.clean'
  | 'scanner.infected'
  | 'scanner.notScanned'
  | 'scanner.released'
  | 'scanner.missing'
  | 'sender.internal'
  | 'sender.external'
  | 'sender.replyToDiffers'
  | 'sender.returnPathDiffers'
  | 'sender.onBehalf'
  | 'sender.nameContainsAddress'
  | 'sender.impersonatesInternal'
  | 'sender.lookalikeDomain'
  | 'sender.punycode';

export interface AnalysisFinding {
  code: FindingCode;
  severity: AnalysisSeverity;
  params: Readonly<Record<string, string>>;
}

export interface AnalysisAddress {
  name: string;
  address: string;
  domain: string;
}

export interface AnalysisAuthResult {
  method: string;
  result: string;
  reason: string | null;
  props: Readonly<Record<string, string>>;
  comment: string | null;
}

export type Alignment = 'strict' | 'relaxed' | 'none';

export interface AnalysisHop {
  index: number;
  fromHost: string | null;
  fromDetail: string | null;
  fromIp: string | null;
  byHost: string | null;
  protocol: string | null;
  id: string | null;
  recipient: string | null;
  tls: { used: boolean | null; version: string | null; cipher: string | null; bits: number | null };
  receivedAt: string | null;
  delaySeconds: number | null;
  clockSkew: boolean;
  internal: boolean;
  ours: boolean;
  raw: string;
}

export interface AnalysisMimePart {
  path: string;
  depth: number;
  contentType: string;
  charset: string | null;
  disposition: 'inline' | 'attachment' | null;
  filename: string | null;
  contentId: string | null;
  transferEncoding: string | null;
  size: number;
  embedded: { subject: string | null; from: string | null } | null;
}

export interface MessageAnalysisView {
  status: AnalysisSeverity;
  findings: readonly AnalysisFinding[];
  source: { origin: 'imap' | 'spliced' | 'ews' | 'reconstructed'; size: number; headersOnly: boolean };
  message: { subject: string | null; date: string | null; messageId: string | null; inReplyTo: string | null; references: readonly string[]; client: string | null };
  sender: {
    from: AnalysisAddress | null;
    sender: AnalysisAddress | null;
    replyTo: readonly AnalysisAddress[];
    returnPath: string | null;
    fromDomainUnicode: string | null;
    external: boolean | null;
  };
  delivery: { hops: readonly AnalysisHop[]; sentAt: string | null; deliveredAt: string | null; totalSeconds: number | null };
  authentication: {
    verifiedByServer: boolean;
    spf: { result: string; domain: string | null; alignment: Alignment; trusted: boolean } | null;
    dkim: readonly {
      domain: string;
      selector: string;
      algorithm: string | null;
      identity: string | null;
      signedAt: string | null;
      signedHeaders: readonly string[];
      result: string | null;
      alignment: Alignment;
    }[];
    dmarc: { result: string; policy: string | null; fromDomain: string | null; trusted: boolean } | null;
    arc: readonly { instance: number; chainValidation: string | null; sealDomain: string | null; authservId: string | null; results: readonly AnalysisAuthResult[] }[];
    foreign: readonly { authservId: string; results: readonly AnalysisAuthResult[] }[];
  };
  scanner: { trusted: boolean; result: string | null; threat: string | null; engine: string | null; scanId: string | null; verdict: string | null } | null;
  /** User-safe projection of the scanner's rule evaluation (rule display names only); absent until the server provides it. */
  policy?: { evaluation: PolicyEvaluationEvidence; ruleNames: Readonly<Record<string, string>> } | null;
  /** Scanner history of this item (bound server-side by the scanner-stamped scan id); absent = no trusted scan id. */
  history?: {
    status: 'recorded' | 'unavailable';
    truncated: boolean;
    events: readonly { ts: string; stage: 'capture' | 'quarantine' | 'release' | 'journal'; outcome: 'success' | 'failure' | 'info' }[];
  };
  spam: readonly { header: string; value: string }[];
  structure: { parts: readonly AnalysisMimePart[]; truncated: boolean } | null;
  headers: readonly { name: string; value: string }[];
}

/** Severity → status colour of `StatusBadge` (DESIGN.md tokens: success, muted, warning, destructive). */
export const SEVERITY_STATUS: Readonly<Record<AnalysisSeverity, Status>> = {
  pass: 'ok',
  info: 'unknown',
  warning: 'warning',
  fail: 'error',
};

/** Authentication result → status colour. */
export function resultStatus(result: string | null | undefined): Status {
  switch (result) {
    case 'pass':
      return 'ok';
    case 'fail':
    case 'permerror':
      return 'error';
    case 'softfail':
    case 'temperror':
    case 'policy':
      return 'warning';
    default:
      return 'unknown';
  }
}
